import {
  DeleteOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Badge,
  Button,
  Col,
  Modal,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import type {
  UserRoleAssignmentCreateEvent,
  UserRoleAssignmentDeleteEvent,
} from '../../hooks/types';
import useDeleteTeamRoleAssignment from '../../hooks/useDeleteTeamRoleAssignment';
import { useLoggedInUser } from '../../hooks/useLoggedInUser';
import { useNotify } from '../../hooks/useNotify';
import { usePermission } from '../../hooks/usePermission';
import { queryTeamRosterName } from '../../hooks/useQueryTeamRoster';
import useSaveUserRoleAssignments from '../../hooks/useSaveUserRoleAssignments';
import { eventData } from '../../lib/graphql/common/queries';
import { successMessage } from '../../lib/utils/messages';
import rfsTheme from '../../theme';
import SearchUserSelect from '../SearchUserSelect';
import { Optional } from '../Shared';
import type { RoleInformation, RosterUserRecord } from './types';
import { onApolloError } from '../../lib/graphql/helpers';

const { Title, Paragraph } = Typography;

type TeamRoleCellProps = {
  readonly title: string;
  readonly assignedUsers: readonly RosterUserRecord[];
  readonly readOnly: boolean;
  readonly teamRoleAssignmentId: string;
  readonly roleInfo?: RoleInformation;
};

const AssignedUsers = ({
  users,
}: {
  readonly users: readonly RosterUserRecord[];
}) => {
  const maxUsersToShow = 10;

  return (
    <>
      <Title style={{ color: '#ffffff' }} level={5}>
        Manage users
      </Title>
      <Optional show={users.length === 0}>
        <Paragraph style={{ color: '#ffffff' }}>No User Assigned</Paragraph>
      </Optional>
      {users.slice(0, maxUsersToShow).map((userRecord) => (
        <Paragraph style={{ color: '#ffffff' }} key={userRecord.key}>{`${
          userRecord.firstName
        } ${userRecord.lastName} (${
          userRecord.user_type ?? 'Unknown Type'
        })`}</Paragraph>
      ))}
      <Optional show={users.length > maxUsersToShow}>
        <Paragraph style={{ color: '#ffffff' }}>
          + {users.slice(maxUsersToShow).length} additional user(s)
        </Paragraph>
      </Optional>
    </>
  );
};

/**
 * Renders a table cell for a particular Team's role.
 * Provides the ability to manage roster team role assignments and user role assignments.
 */
const TeamRoleCell = ({
  title,
  assignedUsers,
  readOnly,
  roleInfo,
  teamRoleAssignmentId,
}: TeamRoleCellProps) => {
  const { notify } = useNotify();
  const assignedUserIds: readonly string[] = assignedUsers.map(
    (user) => user.userId
  );

  const { loggedInUser } = useLoggedInUser();

  const { teamId } = useParams<{ readonly teamId: string | undefined }>();

  const permission = usePermission(teamId, loggedInUser);

  const [deleteTeamRoleAssignment] = useDeleteTeamRoleAssignment(
    teamRoleAssignmentId,
    onApolloError(notify.error)
  );

  const [saveUserRoleAssignments] = useSaveUserRoleAssignments(
    onApolloError(notify.error)
  );

  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  const [showAssignedUsers, setShowAssignedUsers] = useState(false);

  const [selectedUserIds, setSelectedUserIds] =
    useState<readonly string[]>(assignedUserIds);

  const onDeleteTeamRoleAssignment = () => {
    if (loggedInUser !== undefined) {
      void deleteTeamRoleAssignment({
        onCompleted: () => {
          notify.success(successMessage.teamRoleAssignmentDeleted);
        },
        refetchQueries: [queryTeamRosterName],
      });

      toggleDeleteAlert();
    }
  };

  const onSaveAssignedUsers = () => {
    if (loggedInUser !== undefined) {
      const usersIdsToCreate: readonly string[] = selectedUserIds.filter(
        (id) => !assignedUserIds.includes(id)
      );

      const userIdsToDelete: readonly string[] = assignedUserIds.filter(
        (id) => !selectedUserIds.includes(id)
      );

      const usersToDelete: readonly RosterUserRecord[] = assignedUsers.filter(
        (user) => userIdsToDelete.includes(user.userId)
      );

      const userRoleAssignmentsToCreate: readonly UserRoleAssignmentCreateEvent[] =
        usersIdsToCreate.map((userId) => ({
          user_id: userId,
          team_role_assignment_id: teamRoleAssignmentId,
          event: eventData('user_role_assignment_created'),
        }));

      const userRoleAssignmentsToDelete: readonly UserRoleAssignmentDeleteEvent[] =
        usersToDelete.map((user) => ({
          user_role_assignment_id: user.userRoleAssignmentId,
          event: eventData('user_role_assignment_deleted'),
        }));

      void saveUserRoleAssignments({
        variables: {
          userRoleAssignmentsToCreate,
          userRoleAssignmentsToDelete,
        },
        onCompleted: () => {
          notify.success(successMessage.userRoleAssignmentSaved);
        },
        refetchQueries: [queryTeamRosterName],
      });
    }

    toggleAssignedUsers();
  };

  const toggleDeleteAlert = () => {
    setShowDeleteAlert(!showDeleteAlert);
  };

  const toggleAssignedUsers = () => {
    setShowAssignedUsers(!showAssignedUsers);
  };

  const unassignTeamRolePermission = permission.teamAssignTeamRole;
  const assignUserPermission = permission.teamAssignTeamRole;

  return (
    <div style={{ padding: '5px', width: '100%' }}>
      <Row>
        <Col lg={24} xl={14}>
          <Title
            level={3}
            style={{ color: rfsTheme.darkRfsRed, fontSize: '16px' }}
          >
            {title}
          </Title>
        </Col>
        <Optional show={!readOnly}>
          <Col style={{ textAlign: 'right' }} lg={24} xl={8}>
            <Space direction="horizontal">
              <Optional
                show={
                  unassignTeamRolePermission && roleInfo?.roleGroup !== 'SOC'
                }
              >
                <Tooltip
                  title={`Unassign ${roleInfo?.name ?? ''} from roster`}
                  color="#000000"
                >
                  <Button
                    id={`delete-${roleInfo?.teamRoleId ?? ''}-btn`}
                    data-cy={`delete-users-${roleInfo?.name ?? ''}-btn`}
                    shape="circle"
                    icon={<DeleteOutlined />}
                    onClick={toggleDeleteAlert}
                  />
                </Tooltip>
              </Optional>
              <Tooltip
                title={<AssignedUsers users={assignedUsers} />}
                color="#000000"
                // Do not automatically adjust popup placement when popup is off screen
                // to avoid the pop-up ever covering the user assign button.
                autoAdjustOverflow={false}
              >
                <Badge
                  style={{ backgroundColor: rfsTheme.darkRfsRed }}
                  count={assignedUsers.length}
                  showZero
                >
                  <Button
                    id={`assigned-users-${roleInfo?.teamRoleId ?? ''}-btn`}
                    data-cy={`assigned-users-${roleInfo?.name ?? ''}-btn`}
                    shape="circle"
                    icon={<UserOutlined />}
                    onClick={toggleAssignedUsers}
                  />
                </Badge>
              </Tooltip>
            </Space>
          </Col>
        </Optional>
      </Row>
      <Row>
        <Col>
          {roleInfo === undefined ? (
            <></>
          ) : (
            <Tag
              style={{
                whiteSpace: 'normal',
                borderColor: roleInfo.colour,
                color: roleInfo.textColour,
              }}
              color={roleInfo.colour}
            >
              {roleInfo.roleGroup}
            </Tag>
          )}
        </Col>
      </Row>
      <Modal
        destroyOnClose
        title={`Assigned users for ${roleInfo?.name ?? ''} on roster`}
        open={showAssignedUsers}
        okButtonProps={{
          disabled: !assignUserPermission,
          'aria-disabled': !assignUserPermission,
        }}
        onOk={onSaveAssignedUsers}
        onCancel={toggleAssignedUsers}
        okText="Save"
      >
        <Optional show={assignUserPermission}>
          <Alert
            type="warning"
            message={
              <>
                <WarningOutlined /> Note: unassigning personnel will&nbsp;
                <strong>remove</strong> future shifts for that personnel
                as&nbsp;
                <strong>{roleInfo?.name}</strong> from the roster.
              </>
            }
          />
        </Optional>

        <SearchUserSelect
          formName="selectUserForm"
          formListName="assignedUsers"
          fieldName="userIds"
          currentUserIds={assignedUserIds}
          onChange={(selectedUsers) =>
            setSelectedUserIds(selectedUsers.map(({ id }) => id))
          }
        />
      </Modal>
      <Modal
        destroyOnClose
        title={`Unassign ${roleInfo?.name ?? ''} from roster`}
        open={showDeleteAlert}
        onOk={onDeleteTeamRoleAssignment}
        onCancel={toggleDeleteAlert}
        okText="Unassign"
      >
        <Alert
          type="warning"
          message={
            <>
              <WarningOutlined /> Unassigning the&nbsp;
              <strong>{roleInfo?.name}</strong> role will&nbsp;
              <strong>remove</strong> all personnel assignments and shifts for
              this role from the roster.
            </>
          }
        />
      </Modal>
    </div>
  );
};

export default TeamRoleCell;
